import { Box, IconButton, Stack, useTheme } from "@mui/material";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { ListKit } from "@tiptap/extension-list";
import { Strike } from "@tiptap/extension-strike";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { CheckIcon, CloseIcon, Tooltip } from "components";
import { ContentCover } from "components/content-cover";
import {
  memo,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
} from "react";
import { ChatFormattingToolbar } from "./ChatFormattingToolbar";

export interface ChatMessageEditorHandle {
  /** The TipTap editor instance — null until it mounts. */
  getEditor: () => Editor | null;
}

export interface ChatMessageEditorProps {
  initialContent: string;
  onSave: (newContent: string) => void;
  onCancel: () => void;
  isSaving: boolean;
  /** Rendered at the right end of the toolbar (e.g. a mic). */
  endAdornment?: ReactNode;
  /** Covers the toolbar, text and Cancel / Save at their size while set; the editor stays mounted. */
  overlay?: ReactNode;
  editorRef?: Ref<ChatMessageEditorHandle>;
}

export const ChatMessageEditor = memo(function ChatMessageEditor({
  initialContent,
  onSave,
  onCancel,
  isSaving,
  endAdornment,
  overlay,
  editorRef,
}: ChatMessageEditorProps) {
  const theme = useTheme();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        hardBreak: false,
      }),
      Strike,
      ListKit.configure({ taskList: false, taskItem: false }),
      TaskList,
      TaskItem.configure({ nested: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({
        inline: true,
        HTMLAttributes: {
          style:
            "max-width: 180px; max-height: 180px; object-fit: contain; border-radius: 4px;",
        },
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: initialContent,
    autofocus: "end",
    editable: !isSaving,
  });

  // Disable editor while saving
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isSaving);
    }
  }, [editor, isSaving]);

  useImperativeHandle(editorRef, () => ({ getEditor: () => editor }), [editor]);

  // Handle Escape to cancel
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    },
    [onCancel],
  );

  const handleSave = useCallback(() => {
    if (!editor || isSaving) return;
    const html = editor.getHTML();
    onSave(html);
  }, [editor, isSaving, onSave]);

  return (
    <Box
      onKeyDownCapture={handleKeyDown}
      sx={{
        padding: "8px 12px",
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: "6px",
        backgroundColor: theme.palette.neutral.main,
        width: "100%",
        minWidth: "400px",
      }}
    >
      {/* `overlay` covers the toolbar, text and Cancel / Save at their size;
          the editor stays mounted underneath. */}
      <ContentCover cover={overlay}>
        <Stack gap="12px">
          {/* Formatting toolbar */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ChatFormattingToolbar
                editor={editor}
                containerSx={{ px: 0, py: 0 }}
              />
            </Box>
            {endAdornment}
          </Stack>

          {/* Editor input area */}
          <Box
            sx={{
              width: "100%",
              minHeight: "10px",
              overflow: "auto",
              "& .tiptap": {
                outline: "none",
                minHeight: "10px",
                ...theme.typography["body-medium"],
                border: "none",
                padding: "0px",
                wordBreak: "break-word",
              },
              "& .chat-mention": {
                ...theme.typography["tag-label-xs"],
                backgroundColor: "#D2DDFF",
                color: "#5563F5",
                borderRadius: "4px",
                padding: "1px 6px",
              },
              "& .tiptap img": {
                maxWidth: "180px",
                maxHeight: "180px",
                objectFit: "contain",
                borderRadius: "4px",
              },
              "& .tiptap ul, & .tiptap ol": {
                paddingLeft: "20px",
              },
              "& .tiptap blockquote": {
                borderLeft: `3px solid ${theme.palette.neutral["015"]}`,
                paddingLeft: "12px",
                margin: "4px 0",
                color: theme.palette.lightBg.medium,
              },
            }}
          >
            <EditorContent editor={editor} />
          </Box>

          {/* Footer: Cancel + Save */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="16px"
          >
            <Tooltip title="Cancel edit">
              <IconButton
                aria-label="Cancel edit"
                onClick={onCancel}
                disabled={isSaving}
                sx={{ padding: "0px" }}
              >
                <CloseIcon
                  fontSize="small"
                  sx={{ color: theme.palette.lightBg.medium }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save edit">
              <IconButton
                aria-label="Save edit"
                onClick={handleSave}
                disabled={isSaving}
                sx={{ padding: "0px" }}
              >
                <CheckIcon
                  fontSize="small"
                  sx={{
                    color: isSaving
                      ? theme.palette.lightBg.low
                      : theme.palette.lightBg.high,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </ContentCover>
    </Box>
  );
});
